import { Link, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { LogOut, Music2, Upload, LayoutDashboard, User, Users, ListMusic, Settings, BarChart3, DollarSign, Shield, Ticket, Ban, Mail, Link2, KeyRound, Radio, Youtube, HardDrive } from "lucide-react";

export function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [pendingCR, setPendingCR] = useState(0);
  const [pendingTD, setPendingTD] = useState(0);

  useEffect(() => {
    if (user?.role === "admin") {
      const load = () => api.get("/admin/stats").then((r) => {
        setPendingCR(r.data.copyright_pending || 0);
        setPendingTD(r.data.takedown_pending || 0);
      }).catch(() => {});
      load();
      const t = setInterval(load, 30000);
      return () => clearInterval(t);
    }
  }, [user?.role]);

  const onLogout = () => { logout(); nav("/"); };

  const userLinks = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, tid: "nav-dashboard" },
    { to: "/upload", label: "Upload Song", icon: Upload, tid: "nav-upload" },
    { to: "/my-songs", label: "My Songs", icon: ListMusic, tid: "nav-my-songs" },
    { to: "/dsp-links", label: "DSP Links", icon: Radio, tid: "nav-dsp-links" },
    { to: "/youtube-channels", label: "YouTube Channels", icon: Youtube, tid: "nav-yt-channels" },
    { to: "/revenue", label: "Revenue", icon: DollarSign, tid: "nav-revenue" },
    { to: "/copyright", label: "Copyright", icon: Shield, tid: "nav-copyright" },
    { to: "/takedown", label: "Takedown", icon: Ban, tid: "nav-takedown" },
    { to: "/profile", label: "Profile", icon: User, tid: "nav-profile" },
  ];
  const adminLinks = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, tid: "nav-admin-dashboard" },
    { to: "/admin/users", label: "Users", icon: Users, tid: "nav-admin-users" },
    { to: "/admin/submissions", label: "Submissions", icon: ListMusic, tid: "nav-admin-submissions" },
    { to: "/admin/dsp-links", label: "DSP Links", icon: Link2, tid: "nav-admin-dsp-links" },
    { to: "/admin/codes", label: "Codes", icon: Ticket, tid: "nav-admin-codes" },
    { to: "/admin/revenue", label: "Revenue", icon: DollarSign, tid: "nav-admin-revenue" },
    { to: "/admin/yt-revenue", label: "YT Revenue", icon: DollarSign, tid: "nav-admin-yt-revenue" },
    { to: "/admin/youtube-channels", label: "YT Channels", icon: Youtube, tid: "nav-admin-yt-channels" },
    { to: "/admin/copyright", label: "Copyright", icon: Shield, tid: "nav-admin-copyright", badge: pendingCR },
    { to: "/admin/takedown", label: "Takedown", icon: Ban, tid: "nav-admin-takedown", badge: pendingTD },
    { to: "/admin/email-log", label: "Email Log", icon: Mail, tid: "nav-admin-email-log" },
    { to: "/admin/reports", label: "Reports", icon: BarChart3, tid: "nav-admin-reports" },
    { to: "/admin/password-audit", label: "Password Audit", icon: KeyRound, tid: "nav-admin-password-audit" },
    { to: "/admin/storage", label: "Storage", icon: HardDrive, tid: "nav-admin-storage" },
    { to: "/admin/settings", label: "Settings", icon: Settings, tid: "nav-admin-settings" },
  ];

  const links = user ? (user.role === "admin" ? adminLinks : userLinks) : [];

  return (
    <header className="sticky top-0 z-40 glass border-b border-zinc-800/80">
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Link to={user ? (user.role === "admin" ? "/admin" : "/dashboard") : "/"} className="flex items-center gap-2 shrink-0" data-testid="brand-link">
          <img src="/logo.png" alt="Sonotek Digital" data-testid="brand-logo"
            className="h-9 w-auto rounded-md bg-white/95 p-0.5 border border-zinc-800 shadow-sm" />
          <span className="font-bold tracking-tight text-lg hidden sm:block">
            Sonotek<span className="gradient-text">Digi</span>
          </span>
        </Link>

        {user ? (
          <>
            <nav className="hidden lg:flex items-center gap-0.5 overflow-x-auto">
              {links.map((l) => (
                <NavLink key={l.to} to={l.to} data-testid={l.tid} end={l.to === "/admin"}
                  className={({ isActive }) =>
                    `flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors ${
                      isActive ? "bg-zinc-800 text-cyan-400" : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900"
                    }`
                  }
                >
                  <l.icon className="w-3.5 h-3.5" />
                  {l.label}
                  {l.badge > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 rounded-full bg-rose-500/20 border border-rose-500/40 text-rose-300 text-[10px] font-semibold" data-testid={`${l.tid}-badge`}>
                      {l.badge} Pending
                    </span>
                  )}
                </NavLink>
              ))}
            </nav>
            <div className="flex items-center gap-2">
              <div className="hidden sm:flex items-center gap-2 text-xs text-zinc-400">
                <div className="w-7 h-7 rounded-full bg-zinc-800 flex items-center justify-center text-cyan-400 font-semibold">
                  {user.full_name?.[0]?.toUpperCase() || "U"}
                </div>
                <span className="max-w-[100px] truncate">{user.full_name}</span>
              </div>
              <Button variant="ghost" size="sm" onClick={onLogout} data-testid="logout-btn" className="text-zinc-400 hover:text-rose-400">
                <LogOut className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </>
        ) : (
          <nav className="flex items-center gap-1">
            <NavLink to="/" data-testid="nav-home" className={({isActive})=>`px-3 py-1.5 rounded-full text-sm ${isActive?'text-cyan-400':'text-zinc-300 hover:text-white'}`}>Home</NavLink>
            <NavLink to="/about" data-testid="nav-about" className={({isActive})=>`px-3 py-1.5 rounded-full text-sm ${isActive?'text-cyan-400':'text-zinc-300 hover:text-white'}`}>About</NavLink>
            <Link to="/login" data-testid="nav-login">
              <Button size="sm" className="rounded-full ml-1 bg-gradient-to-r from-cyan-400 to-violet-500 hover:brightness-110 text-zinc-950 font-semibold">
                Artist Login
              </Button>
            </Link>
          </nav>
        )}
      </div>
      {/* Mobile nav */}
      {user && (
        <div className="lg:hidden border-t border-zinc-800/80 overflow-x-auto">
          <div className="flex items-center gap-1 px-3 py-2 min-w-max">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} data-testid={`${l.tid}-mobile`} end={l.to === "/admin"}
                className={({ isActive }) =>
                  `flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs whitespace-nowrap ${
                    isActive ? "bg-zinc-800 text-cyan-400" : "text-zinc-400"
                  }`
                }
              >
                <l.icon className="w-3.5 h-3.5" /> {l.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
